<script>
  import { createEventDispatcher, onMount, tick } from "svelte";
  import { fly, fade } from "svelte/transition";
  import { tourFunctions } from "../tour-functions";
  import { focusTrap } from "./utils.js";
  import CloseButton from "./CloseButton.svelte";

  export let ariaLabel = "Popup";
  export let visible = true;
  export let width = null;
  export let textColor = null;
  export let bgColor = null;

  let popupModal;
  let scrollContainer;
  const dispatch = createEventDispatcher();

  // Dispatch events on open and close
  $: visible ? dispatch("open") : dispatch("close");
  $: if (visible) focusPopup();
  // Create a focus trap
  async function focusPopup() {
    await tick();
    scrollContainer.focus();
  }

  function closePopup() {
    visible = false;
    tourFunctions.focus();
  }

  function getPopupTextColor() {
    if (textColor) return textColor;
    // get --popup-text-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupTextColor = rootStyles.getPropertyValue("--popup-text-color");
    return popupTextColor || "#000";
  }

  function getPopupBgColor() {
    if (bgColor) return bgColor;
    // get --popup-bg-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupBgColor = rootStyles.getPropertyValue("--popup-bg-color");
    return popupBgColor || "#fff";
  }
</script>

{#if visible}
  <div id="popup-gallery"></div>

  <div bind:this={popupModal} class="popup-modal" use:focusTrap>
    <div class="popup-area">
      <div class="popup-window-wrap">
        <div
          class="popup-window"
          transition:fly={{ y: 800, duration: 500 }}
          style={`
            ${width ? `width: ${width};` : ""}
            --popup-text-color: ${getPopupTextColor()};
            --popup-bg-color: ${getPopupBgColor()};
          `}
        >
          <div class="popup-content">
            <div class="popup">
              <CloseButton close={closePopup} color={getPopupTextColor()} />

              <div class="content column">
                <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
                <div
                  class="content-inner"
                  role="dialog"
                  aria-label={ariaLabel}
                  tabindex="0"
                  bind:this={scrollContainer}
                >
                  <slot {scrollContainer} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="popup-bg"
        on:click={closePopup}
        transition:fade={{ duration: 500 }}
      ></div>
    </div>
  </div>
{/if}

<style>
  .popup {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  /* :global(.popup-content *:focus) {
    outline: 2px dotted var(--popup-text-color);
  } */

  .column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
    height: 100%;
    min-width: 0; /* Allow flex item to shrink below content size */
  }

  .content {
    justify-content: center;
    font-family: var(--font-body), sans-serif;
    line-height: 1.5;
  }

  .content-inner {
    padding: 2rem;
    padding-top: 0;
    padding-right: 5rem; /* Add space for close button (50px button + 20px right position + margin) */
    overflow-y: scroll;
    max-height: 90vh;
    box-sizing: border-box;
    width: 100%;
    min-width: 0; /* Allow content to shrink */
  }

  .content-inner:focus {
    outline: none;
  }

  :global(.popup .content-inner a) {
    color: var(--popup-text-color);
    font-weight: 700;
  }

  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--popup-text-color);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--popup-text-color);
  }

  .popup-area {
    position: fixed;
    z-index: 2001;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100vw;
    height: 100vh;
  }

  .popup-bg {
    position: fixed;
    z-index: 2000;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.66);
  }

  .popup-window-wrap {
    position: relative;
    margin: 2rem;
    max-height: 100%;
    box-sizing: border-box;
  }

  .popup-window {
    position: relative;
    z-index: 99999;
    width: 40rem;
    max-width: 100%;
    max-height: 100%;
    margin: 2rem auto;
    border-radius: 10px;
    background: var(--popup-bg-color);
    color: var(--popup-text-color);
    overflow: hidden;
  }

  .popup-content {
    border-radius: 5px;
    padding: 0;
    height: 100%;
    width: 100%;
  }

  @media only screen and (max-width: 600px) {
    .popup-area {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;
      width: 90%;
      height: 90%;
    }

    .popup-window-wrap,
    .popup-window {
      width: 100% !important;
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
    }

    .popup {
      position: relative;
      height: 100%;
    }

    .column {
      width: 100%;
      flex: none;
    }

    .image.column {
      min-height: 40%;
      height: fit-content;
      aspect-ratio: 1/1;
    }

    .content {
      justify-content: flex-start;
      font-family: var(--font-body), sans-serif;
      line-height: 1.5;
    }

    .content-inner {
      font-size: 14px;
      height: 100%;
    }
  }
</style>
